import React, { useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { Contact } from 'v2/models';
import classnames from 'classnames';
import styles from 'v2/pages/ContactsDetails/ContactDetailTab/contactDetailTab.module.scss';
import MarkdownRender from 'components/MarkdownRender/MarkdownRender';
import EditContactModal, {
  EditionTypesText,
} from './EditContactDetails/EditContactModal';
import SectionHeader from 'v2/common/DRM/SectionHeader/SectionHeader';
import useGetStrapiUser from 'v2/hooks/useGetOwner';
import { useTranslation } from 'react-i18next';
import { PostProcessKeys, TranslationKeys } from 'translations';

interface ContactTabOwnProps {
  contact: Contact;
  refetch: () => void;
}

const ContactDetailTab = ({ contact, refetch }: ContactTabOwnProps) => {
  const { t } = useTranslation();
  const [currentEditionType, setCurrentEditionType] = useState<{
    title: string;
    editionType: EditionTypesText;
  } | null>(null);

  const { data: contactOwnerData, isFetching: contactOwnerIsFetching } =
    useGetStrapiUser(contact.contact_owner);

  if (contactOwnerIsFetching) {
    return (
      <div className={classnames(styles.loading)}>
        <Spinner animation="border" />
      </div>
    );
  }

  function noDataField(text: string) {
    return (
      <label
        data-testid={`noData_${text.replaceAll(' ', '')}`}
        className={styles.noData}
      >{`${text} for this ${t(TranslationKeys.contact_one)}.`}</label>
    );
  }

  const handleEditClose = () => {
    setCurrentEditionType(null);
    refetch();
  };

  return (
    <>
      <div
        className={styles.fieldsContainer}
        data-cy="contact-details:details-tab-container"
      >
        <div className={styles.field}>
          <SectionHeader
            title="Address"
            isEditable={contact.is_editable}
            className="mb-3"
            onClick={() =>
              setCurrentEditionType({
                editionType: EditionTypesText.address,
                title: 'Edit Address',
              })
            }
          />
          {contact.address ? (
            <div
              data-testid={`value_Address`}
              className={styles.descriptionTexts}
            >
              <MarkdownRender
                className="markdown-text-blue-light pr-4"
                source={contact.address}
                format={false}
              />
            </div>
          ) : (
            noDataField('Enter an address')
          )}
        </div>
        <div className={styles.field}>
          <SectionHeader
            title="Phone Number"
            isEditable={contact.is_editable}
            className="mb-3"
            onClick={() =>
              setCurrentEditionType({
                editionType: EditionTypesText.phone_number,
                title: 'Edit Phone Number',
              })
            }
          />
          {contact.phone_number ? (
            <div
              data-testid={`value_Phone`}
              className={styles.descriptionTexts}
            >
              {contact.phone_number}
            </div>
          ) : (
            noDataField('Enter a phone number')
          )}
        </div>
        <div className={styles.field}>
          <SectionHeader
            title="Linkedin"
            isEditable={contact.is_editable}
            className="mb-3"
            onClick={() =>
              setCurrentEditionType({
                editionType: EditionTypesText.linkedin,
                title: 'Edit Linkedin',
              })
            }
          />
          {contact.linkedin ? (
            <a
              href={`${contact.linkedin}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {contact.linkedin}
            </a>
          ) : (
            noDataField('Enter a linkedin')
          )}
        </div>
        <div className={styles.field}>
          <SectionHeader
            title={`${t(TranslationKeys.contact_one, {
              postProcess: PostProcessKeys.TitleCase,
            })} Owner`}
            isEditable={contact.is_editable}
            className="mb-3"
            onClick={() =>
              setCurrentEditionType({
                editionType: EditionTypesText.contact_owner,
                title: `Edit ${t(TranslationKeys.contact_one, {
                  postProcess: PostProcessKeys.TitleCase,
                })} Owner`,
              })
            }
          />
          {contactOwnerData ? (
            <div
              data-testid={`value_Owner`}
              className={styles.descriptionTexts}
            >
              <div>
                <div className={classnames(styles.ownerHeader)}>
                  {contactOwnerData?.first_name} {contactOwnerData?.last_name}
                </div>
                <div className={classnames(styles.ownerDescription)}>
                  {contactOwnerData?.job_title && (
                    <p>{contactOwnerData?.job_title}</p>
                  )}
                  {contactOwnerData?.email && (
                    <p>
                      <a href={`mailto:${contactOwnerData?.email}`}>
                        {contactOwnerData?.email}
                      </a>
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            noDataField(`Enter a ${t(TranslationKeys.contact_one)} owner`)
          )}
        </div>
      </div>
      {currentEditionType && (
        <EditContactModal
          show={currentEditionType.editionType !== null}
          contact={contact}
          editionType={currentEditionType.editionType}
          onClose={handleEditClose}
          title={currentEditionType.title}
          contactOwner={contactOwnerData}
        />
      )}
    </>
  );
};

export default ContactDetailTab;
